import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { clearKioskSession, kioskLogin, readKioskSession, refreshKioskSession } from "../lib/kioskSession";
import "../styles/eventCheckinKiosk.css";

const COPY = {
  vi: {
    eyebrow: "KIOSK NHÂN VIÊN",
    title: "ĐĂNG NHẬP KIOSK",
    title_en: "Event Kiosk Login",
    subtitle: "Nhập mã sự kiện và mã PIN nhân viên để mở kiosk check-in.",
    subtitle_en: "Enter the event code and staff PIN to unlock the check-in kiosk.",
    code_label: "Mã Sự Kiện",
    code_en: "Event code",
    code_placeholder: "VD: ELK2026",
    pin_label: "Mã PIN Nhân Viên",
    pin_en: "Staff PIN",
    pin_placeholder: "••••",
    submit: "MỞ KIOSK",
    submit_en: "UNLOCK KIOSK",
    submitting: "Đang kiểm tra…",
    nvc: "NAIL VIET CLUB",
    motto: "KẾT NỐI • HỌC HỎI • PHÁT TRIỂN",
    powered: "POWERED BY",
    note: "Kiosk sẽ tự động khoá phiên sau 12 giờ.",
    note_en: "Kiosk auto-locks after 12 hours.",
  },
  en: {
    eyebrow: "STAFF KIOSK",
    title: "EVENT KIOSK LOGIN",
    title_en: "ĐĂNG NHẬP KIOSK",
    subtitle: "Enter the event code and staff PIN to unlock the check-in kiosk.",
    subtitle_en: "Nhập mã sự kiện và mã PIN nhân viên để mở kiosk.",
    code_label: "Event Code",
    code_en: "Mã Sự Kiện",
    code_placeholder: "e.g. ELK2026",
    pin_label: "Staff PIN",
    pin_en: "Mã PIN",
    pin_placeholder: "••••",
    submit: "UNLOCK KIOSK",
    submit_en: "MỞ KIOSK",
    submitting: "Verifying…",
    nvc: "NAIL VIET CLUB",
    motto: "CONNECT • LEARN • GROW",
    powered: "POWERED BY",
    note: "Session auto-locks after 12 hours.",
    note_en: "",
  },
};

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return null;
}

export default function KioskLoginPage() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => (localStorage.getItem("nailEvent.lang") === "en" ? "en" : "vi"));
  const t = COPY[lang];
  const [eventCode, setEventCode] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If a kiosk session already exists, validate it server-side before redirecting.
  // Invalid/expired sessions are cleared and the login form is shown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const local = readKioskSession();
      if (!local?.event?.slug) return;
      const fresh = await refreshKioskSession();
      if (cancelled) return;
      if (fresh?.event?.slug) {
        navigate(`/events/${fresh.event.slug}/checkin-kiosk`, { replace: true });
      } else {
        // Server rejected the token — wipe localStorage and stay on the login screen.
        clearKioskSession();
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  useEffect(() => { localStorage.setItem("nailEvent.lang", lang); }, [lang]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    if (!eventCode.trim() || !pin.trim()) {
      setError(lang === "en" ? "Please enter both event code and PIN." : "Vui lòng nhập mã sự kiện và mã PIN.");
      return;
    }
    setLoading(true);
    try {
      const sess = await kioskLogin({ event_code: eventCode.trim(), pin: pin.trim() });
      navigate(`/events/${sess.event.slug}/checkin-kiosk`, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err?.message ||
        (lang === "en" ? "Login failed. Check the event code and PIN." : "Đăng nhập thất bại. Vui lòng kiểm tra mã sự kiện và PIN.");
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="kiosk-login-root" data-testid="kiosk-login-page">
      {/* Background visuals */}
      <div className="kiosk-login-bg" aria-hidden="true">
        <div className="kiosk-login-glow kiosk-login-glow-gold" />
        <div className="kiosk-login-glow kiosk-login-glow-emerald" />
        <div className="kiosk-login-curves">
          <span className="kiosk-login-curve curve-a" />
          <span className="kiosk-login-curve curve-b" />
        </div>
      </div>

      <div className="kiosk-login-shell">
        {/* Brand strip */}
        <header className="kiosk-login-brand">
          <div className="kiosk-login-nvc">
            <span className="kiosk-login-nvc-mark">NVC</span>
          </div>
          <div className="kiosk-login-name">{t.nvc}</div>
          <div className="kiosk-login-motto">{t.motto}</div>
        </header>

        {/* Card */}
        <main className="kiosk-login-card" data-testid="kiosk-login-card">
          <div className="kiosk-lang" style={{ position: "absolute", top: 18, right: 18 }}>
            <button type="button" data-testid="kiosk-login-lang-vi"
              className={`kiosk-lang-btn ${lang === "vi" ? "active" : ""}`}
              onClick={() => setLang("vi")}>VI</button>
            <button type="button" data-testid="kiosk-login-lang-en"
              className={`kiosk-lang-btn ${lang === "en" ? "active" : ""}`}
              onClick={() => setLang("en")}>EN</button>
          </div>

          <div className="kiosk-card-icon">
            <Lock className="kiosk-card-icon-svg" />
          </div>

          <div className="kiosk-login-eyebrow">
            <Sparkles className="w-3 h-3" />
            <span>{t.eyebrow}</span>
          </div>
          <h1 className="kiosk-card-title">{t.title}</h1>
          <div className="kiosk-card-title-en">{t.title_en}</div>
          <p className="kiosk-card-sub" style={{ marginTop: 18 }}>{t.subtitle}</p>
          <p className="kiosk-card-sub-en">{t.subtitle_en}</p>

          <form className="kiosk-login-form" onSubmit={onSubmit} data-testid="kiosk-login-form">
            <label className="kiosk-login-field">
              <span className="kiosk-login-field-label">
                {t.code_label} <span className="kiosk-login-field-en">/ {t.code_en}</span>
              </span>
              <input
                type="text"
                data-testid="kiosk-login-event-code"
                value={eventCode}
                onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                placeholder={t.code_placeholder}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck="false"
                inputMode="text"
                className="kiosk-login-input"
                required
              />
            </label>

            <label className="kiosk-login-field">
              <span className="kiosk-login-field-label">
                {t.pin_label} <span className="kiosk-login-field-en">/ {t.pin_en}</span>
              </span>
              <input
                type="password"
                data-testid="kiosk-login-pin"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 12))}
                placeholder={t.pin_placeholder}
                inputMode="numeric"
                autoComplete="off"
                className="kiosk-login-input kiosk-login-pin-input"
                required
              />
            </label>

            {error && <div className="kiosk-login-error" data-testid="kiosk-login-error">{error}</div>}

            <button
              type="submit"
              data-testid="kiosk-login-submit"
              disabled={loading}
              className="kiosk-cta kiosk-login-cta"
            >
              {loading
                ? (<><Loader2 className="kiosk-cta-icon kiosk-loading-spin" /><span className="kiosk-cta-vi">{t.submitting}</span></>)
                : (<><ShieldCheck className="kiosk-cta-icon" /><span className="kiosk-cta-vi">{t.submit}</span><ArrowRight className="kiosk-cta-icon" /></>)}
            </button>

            <div className="kiosk-login-note">{t.note} {t.note_en && <span style={{ opacity: 0.6 }}>· {t.note_en}</span>}</div>
          </form>
        </main>

        <footer className="kiosk-login-footer">
          <span className="kiosk-login-powered">{t.powered}</span>
          <span className="kiosk-asterpos" style={{ marginLeft: 8 }}>AsterPOS Corp</span>
        </footer>
      </div>
    </div>
  );
}
